import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DashLayout from "@/components/DashLayout";
import BodyMap from "@/components/BodyMap";
import api from "@/lib/api";
import { toast } from "sonner";
import { ClipboardList, Send, ArrowLeft, CheckCircle2, AlertCircle } from "lucide-react";

/**
 * Patient-side intake questionnaire. Two routes support this page:
 *   /app/intake        -> list of pending intakes
 *   /app/intake/:id    -> fill/submit intake for a specific assessment draft
 */
export default function PatientIntake() {
  const { t } = useTranslation();
  const { id } = useParams();
  const nav = useNavigate();
  const [pending, setPending] = useState([]);
  const [assessment, setAssessment] = useState(null);
  const [form, setForm] = useState({});
  const [bodyMarks, setBodyMarks] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (id) {
      api.get(`/assessments/${id}`).then((r) => {
        setAssessment(r.data);
        setForm(r.data.intake || {});
        setBodyMarks(r.data.intake?.body_map || []);
      }).catch(() => toast.error("Cannot open intake"));
    } else {
      api.get("/patient/pending-intakes").then((r) => setPending(r.data)).catch(() => {});
    }
  }, [id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/assessments/${id}/intake`, { intake: { ...form, body_map: bodyMarks } });
      toast.success(t("pIntake.submitOk"));
      nav("/app/intake");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("pIntake.submitFail"));
    } finally { setBusy(false); }
  };

  if (!id) {
    return (
      <DashLayout>
        <div className="mb-8">
          <div className="fl-label text-[#f47a20]">{t("pIntake.eyebrow")}</div>
          <h1 className="fl-heading text-4xl mt-2">{t("pIntake.h1")}</h1>
          <p className="text-sm mt-2 max-w-2xl" style={{ color: "var(--fl-muted)" }}>
            {t("pIntake.subtitle")}
          </p>
        </div>
        {pending.length === 0 ? (
          <div className="fl-card p-10 text-center" data-testid="intake-empty">
            <CheckCircle2 size={28} className="mx-auto mb-2 text-green-500" />
            <div className="fl-heading text-lg">{t("pIntake.empty")}</div>
            <div className="text-sm mt-1" style={{ color: "var(--fl-muted)" }}>
              {t("pIntake.emptyBody")}
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {pending.map((a) => (
              <button
                key={a.id}
                onClick={() => nav(`/app/intake/${a.id}`)}
                className="fl-card p-4 w-full text-left hover:border-[color:var(--fl-orange)] transition-all"
                data-testid={`intake-item-${a.id.slice(0, 8)}`}
              >
                <div className="flex items-start gap-3">
                  <ClipboardList size={18} className="text-[color:var(--fl-orange)] mt-1" />
                  <div className="flex-1">
                    <div className="fl-heading text-base">
                      {t(`assess.types.${a.assessment_type}`, a.service || a.assessment_type)} {t("pIntake.intakeSuffix")}
                    </div>
                    <div className="text-xs mt-1" style={{ color: "var(--fl-muted)" }}>
                      {t("pIntake.clinicianLabel")}: {a.clinician_name} · {t("pIntake.scheduledLabel")}: {new Date(a.appointment_scheduled_at || a.created_at).toLocaleString()}
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
                        style={{ background: "#F59E0B22", color: "#F59E0B", border: "1px solid #F59E0B55" }}>
                    <AlertCircle size={10} /> {t("pIntake.actionNeeded")}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </DashLayout>
    );
  }

  if (!assessment) return <DashLayout><div className="text-sm">Loading…</div></DashLayout>;

  const locked = ["signed", "locked"].includes(assessment.status);

  return (
    <DashLayout>
      <button onClick={() => nav("/app/intake")} className="inline-flex items-center gap-1 text-xs mb-3 hover:text-[color:var(--fl-orange)]" data-testid="btn-back-intake">
        <ArrowLeft size={14} /> {t("pIntake.backList")}
      </button>
      <div className="mb-6">
        <div className="fl-label text-[#f47a20]">{t("pIntake.formEyebrow")} · {t(`assess.types.${assessment.assessment_type}`, assessment.service || assessment.assessment_type)}</div>
        <h1 className="fl-heading text-3xl mt-2">{t("pIntake.formH1")}</h1>
        <p className="text-sm mt-2 max-w-2xl" style={{ color: "var(--fl-muted)" }}>
          {t("pIntake.formSubtitle")}
        </p>
      </div>

      <div className="space-y-4">
        <Card title={t("pIntake.cardBrings")}>
          <F label={t("pIntake.f.mainProblem")} required>
            <Textarea value={form.main_problem} onChange={(v) => set("main_problem", v)} locked={locked} testid="in-main" />
          </F>
          <F label={t("pIntake.f.reasonBooking")}>
            <Textarea value={form.reason_booking} onChange={(v) => set("reason_booking", v)} locked={locked} />
          </F>
          <div className="grid grid-cols-2 gap-3">
            <F label={t("pIntake.f.symptomsStarted")}>
              <Input type="date" value={form.symptoms_started} onChange={(v) => set("symptoms_started", v)} locked={locked} />
            </F>
            <F label={t("pIntake.f.mechanism")}>
              <Input value={form.mechanism} onChange={(v) => set("mechanism", v)} locked={locked} />
            </F>
          </div>
        </Card>

        <Card title={t("pIntake.cardHurt")}>
          <div className="text-[11px] mb-2" style={{ color: "var(--fl-muted-2)" }}>
            {t("pIntake.hurtHint")}
          </div>
          <BodyMap value={bodyMarks} onChange={setBodyMarks} locked={locked} testid="intake-bodymap" />
        </Card>

        <Card title={t("pIntake.cardPain")}>
          <div className="grid grid-cols-3 gap-3">
            <F label={t("pIntake.f.painNow")}><Input type="number" min={0} max={10} value={form.pain_now} onChange={(v) => set("pain_now", v)} locked={locked} testid="pain-now" /></F>
            <F label={t("pIntake.f.painWorst")}><Input type="number" min={0} max={10} value={form.pain_worst} onChange={(v) => set("pain_worst", v)} locked={locked} /></F>
            <F label={t("pIntake.f.painBest")}><Input type="number" min={0} max={10} value={form.pain_best} onChange={(v) => set("pain_best", v)} locked={locked} /></F>
          </div>
          <F label={t("pIntake.f.aggravating")}><Textarea value={form.aggravating} onChange={(v) => set("aggravating", v)} locked={locked} /></F>
          <F label={t("pIntake.f.easing")}><Textarea value={form.easing} onChange={(v) => set("easing", v)} locked={locked} /></F>
        </Card>

        <Card title={t("pIntake.cardHealth")}>
          <F label={t("pIntake.f.conditions")}><Textarea value={form.conditions} onChange={(v) => set("conditions", v)} locked={locked} /></F>
          <F label={t("pIntake.f.surgery")}><Textarea value={form.surgery} onChange={(v) => set("surgery", v)} locked={locked} /></F>
          <F label={t("pIntake.f.medication")}><Textarea value={form.medication} onChange={(v) => set("medication", v)} locked={locked} /></F>
          <F label={t("pIntake.f.allergies")}><Input value={form.allergies} onChange={(v) => set("allergies", v)} locked={locked} /></F>
          <F label={t("pIntake.f.investigations")}><Textarea value={form.investigations} onChange={(v) => set("investigations", v)} locked={locked} /></F>
          <F label={t("pIntake.f.prevTreatment")}><Textarea value={form.prev_treatment} onChange={(v) => set("prev_treatment", v)} locked={locked} /></F>
        </Card>

        <Card title={t("pIntake.cardLife")}>
          <div className="grid grid-cols-2 gap-3">
            <F label={t("pIntake.f.occupation")}><Input value={form.occupation} onChange={(v) => set("occupation", v)} locked={locked} /></F>
            <F label={t("pIntake.f.sport")}><Input value={form.sport} onChange={(v) => set("sport", v)} locked={locked} /></F>
          </div>
          <F label={t("pIntake.f.activityLevel")}><Input value={form.activity_level} onChange={(v) => set("activity_level", v)} locked={locked} placeholder={t("pIntake.activityPlaceholder")} /></F>
          <F label={t("pIntake.f.training")}><Textarea value={form.training} onChange={(v) => set("training", v)} locked={locked} /></F>
          <F label={t("pIntake.f.limitations")}><Textarea value={form.limitations} onChange={(v) => set("limitations", v)} locked={locked} /></F>
        </Card>

        <Card title={t("pIntake.cardGoals")}>
          <F label={t("pIntake.f.shortGoals")}><Textarea value={form.short_goals} onChange={(v) => set("short_goals", v)} locked={locked} testid="short-goals" /></F>
          <F label={t("pIntake.f.longGoals")}><Textarea value={form.long_goals} onChange={(v) => set("long_goals", v)} locked={locked} /></F>
          <F label={t("pIntake.f.expectations")}><Textarea value={form.expectations} onChange={(v) => set("expectations", v)} locked={locked} /></F>
        </Card>

        <Card title={t("pIntake.cardEmergency")}>
          <F label={t("pIntake.f.emergency")}><Input value={form.emergency_contact} onChange={(v) => set("emergency_contact", v)} locked={locked} /></F>
          <F label={t("pIntake.f.gp")}><Input value={form.gp} onChange={(v) => set("gp", v)} locked={locked} /></F>
        </Card>

        {form._submitted_at && (
          <div className="fl-card p-3 text-xs flex items-center gap-2" style={{ background: "color-mix(in oklab, #22C55E 6%, transparent)", borderColor: "#22C55E55" }}>
            <CheckCircle2 size={14} className="text-green-500" />
            <span>{t("pIntake.submittedNote", { time: new Date(form._submitted_at).toLocaleString() })}</span>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button onClick={() => nav("/app/intake")} className="fl-btn-ghost px-4 py-2 text-sm rounded-md">{t("pIntake.saveClose")}</button>
          <button onClick={submit} disabled={busy || locked} className="fl-btn px-4 py-2 text-sm rounded-md inline-flex items-center gap-1" data-testid="btn-submit-intake">
            <Send size={14} /> {busy ? t("pIntake.submitting") : t("pIntake.submit")}
          </button>
        </div>
      </div>
    </DashLayout>
  );
}

/* ---- Local UI ---- */
function Card({ title, children }) {
  return (
    <section className="fl-card p-4 md:p-5 space-y-3">
      <div className="fl-heading text-base">{title}</div>
      {children}
    </section>
  );
}
function F({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-xs mb-1" style={{ color: "var(--fl-muted)" }}>
        {label}{required && <span className="ml-0.5" style={{ color: "var(--fl-orange)" }}>*</span>}
      </div>
      {children}
    </label>
  );
}
function Input({ value, onChange, type = "text", locked, testid, ...rest }) {
  return (
    <input
      type={type}
      value={value ?? ""}
      onChange={(e) => onChange(type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)}
      disabled={locked}
      className="fl-input w-full text-sm px-3 py-2 rounded-md disabled:opacity-60"
      data-testid={testid}
      {...rest}
    />
  );
}
function Textarea({ value, onChange, locked, testid }) {
  return (
    <textarea
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      disabled={locked}
      rows={3}
      className="fl-input w-full text-sm px-3 py-2 rounded-md disabled:opacity-60"
      data-testid={testid}
    />
  );
}
